<script setup>
import Birthday from 'assets/icons/birthday.svg'
import Delete from 'assets/icons/error.svg'
import Pin from 'assets/icons/pin.svg'

const props = defineProps({
    inputConfig: {
        type: Object,
        required: false,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                label: rawValue => rawValue?.label === undefined || typeof rawValue?.label === 'string',
                id: rawValue => typeof rawValue?.id === 'string',
                lightMode: rawValue => rawValue?.lightMode === undefined || typeof rawValue?.lightMode === 'boolean',
                name: rawValue => typeof rawValue?.name === 'string',
                fieldType: rawValue => typeof rawValue?.fieldType === 'string',
                readonly: rawValue => rawValue?.readonly === undefined || typeof rawValue?.readonly === 'boolean',
                required: rawValue => rawValue?.required === undefined || (typeof rawValue?.required === 'string' && rawValue?.required === 'required'),
                placeholder: rawValue => rawValue?.placeholder === undefined || typeof rawValue?.placeholder === 'string',
                pending: rawValue => rawValue?.pending === undefined || typeof rawValue?.pending === 'boolean',
                disabled: rawValue => rawValue?.disabled === undefined || typeof rawValue?.disabled === 'boolean',
                validationRules: rawValue => rawValue?.validationRules === undefined || typeof rawValue?.validationRules === 'object',
                helperText: rawValue => rawValue?.helperText === undefined || typeof rawValue?.helperText === 'string',
                maxLength: rawValue => rawValue?.maxLength === undefined || typeof rawValue?.maxLength === 'number',
                isError: rawValue => rawValue?.isError === undefined || typeof rawValue?.isError === 'boolean',
            }

            return Object.values(validations).every(validation => validation(rawValue))
        }
    }
})

// Define error alert
const errorAlert = useState('error')

// Define input ref
const input = ref(null)

// Check if input is date
const isDate = computed(() => props.inputConfig?.fieldType === 'date')

// UseField
// validate
const { value, errorMessage, meta, resetField } = useField(() =>
    props.inputConfig?.name, props.inputConfig?.validationRules
)

// Show error + success classes in case fields have validation rules
const requiresValidation = computed(() => props.inputConfig?.validationRules !== undefined)
const isValid = computed(() => meta.valid && meta.dirty && requiresValidation.value)

// Compute isError property
const isError = computed(() => (errorMessage.value && (!meta.valid) && requiresValidation.value))

// Show error + success classes in case fields have validation rules
const formFieldClass = computed(() => {
    return {
        'form-error': isError.value || props.inputConfig?.isError,
        'form-success': isValid.value
    }
})

// Change initial value with dynamic data of input field
resetField({
    value: props.inputConfig?.val
})

// Use watcher to update data with dynamic values
watch(() => props.inputConfig?.val, () => {
    resetField({
        value: props.inputConfig?.val
    })
})

const resetInputField = () => {
    emit('update:inputValue', '')
    errorAlert.value = null
}

const emit = defineEmits(['update:inputValue'])

// Compute helper config
const formHelperConfig = computed(() => {
    return {
        helperText: props.inputConfig.helperText,
        maxLength: props.inputConfig.maxLength,
        value: value.value,
    }
})
</script>

<template>
    <div
        class="form-input-text-wrapper"
        :class="formFieldClass"
        :data-readonly="inputConfig.readonly"
    >
        <label v-if="inputConfig.label" :for="inputConfig.id" class="form-input-text-label">
            {{ inputConfig.label }}:
            <span v-if="inputConfig.required" class="asterisk-icon">*</span>
            <span v-else class="optional">(optional)</span>
        </label>
        <div class="form-input-text-container">
            <Pin v-if="inputConfig.lightMode" class="pin-icon" />

            <input
                :id="inputConfig.id"
                ref="input"
                v-model="value"
                :readonly="inputConfig.readonly"
                :required="inputConfig.required"
                :name="inputConfig.name"
                :type="inputConfig.fieldType"
                :maxlength="inputConfig.maxLength"
                :placeholder="inputConfig.placeholder"
                :disabled="inputConfig.disabled || inputConfig.pending"
                class="form-input-text"
                :class="inputConfig.lightMode ? 'form-input-text-light' : ''"
                @input="emit('update:inputValue', value)"
            >

            <span
                v-if="inputConfig?.resetInputFieldAction && value"
                :title="inputConfig?.resetInputFieldAction?.label"
                class="form-input-delete"
                @click="resetInputField"
            >
                <Delete />
            </span>

            <span v-if="isDate" class="form-input-date-icon" :class="formFieldClass" @click="showDatePicker">
                <Birthday />
            </span>

            <LoadingSpinner v-if="inputConfig.pending" class="form-loader" />
        </div>

        <FormFieldMessage
            :msg-config="{
                message: errorMessage,
                error: (errorMessage && !meta.valid)
            }"
        />

        <FormHelper v-if="inputConfig.helperText || inputConfig.maxLength" :form-helper-config="formHelperConfig" />
    </div>
</template>

<style lang="scss">
.form-input-text-wrapper {
    position: relative;
}

.form-input-text-container {
    position: relative;

    &:has(.form-input-text-light) {
        position: relative;

        &::after {
            content: '';
            position: absolute;
            background: $form-input-light-mode-bg;
            top: 2px;
            right: 2px;
            bottom: 2px;
            width: 50%;
            max-width: 150px;
            z-index: 0;
            border-radius: 10px;
            pointer-events: none;
        }
    }
}

.form-input-text-label {
    @include form-label;
}

.form-input-success-icon,
.form-input-date-icon {
    @include form-icon;
}

.form-input-delete {
    position: absolute;
    z-index: 2;
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    top: 2px;
    right: 2px;
    opacity: 1;
    cursor: pointer;
    pointer-events: all;

    svg {
        width: 20px;
        height: 20px;
        fill: $form-input-action-color;

        .form-error & {
            fill: $form-input-action-color-error;
        }
    }
}

// Safari only hack.. weird & ugly
@supports (-webkit-hyphens:none) {
    .form-input-date-icon {
        pointer-events: none;
        cursor: auto;
    }
}

.form-input-text {
    @include inputfield;

    &.form-input-text-light  {
        @include inputfield($lightMode: 'true');
    }

    .map-control-input &  {
        white-space: nowrap;
        overflow-x: auto;
        padding-inline-end: 60px;
    }
}

.form-input-helper {
    display: grid;
    grid-template-columns: 1fr auto;
}

.helper-text {
    @include helper-text;
}

.char-counter {
    @include char-counter
}

.pin-icon {
    position: absolute;
    top: 12px;
    left: 10px;
    z-index: 2;
    width: 30px;
    height: 30px;
    fill: $pin-icon-fill;

    .form-error & {
        fill: $pin-icon-fill-error;
    }
}
</style>
